import React, { useMemo } from 'react';

import { Box, type SxProps, type Theme } from '@mui/material';
import * as DOMPurify from 'dompurify';
import { RichTextReadOnly } from 'mui-tiptap';

import { useExtensions } from '@ivy/components/molecules/TextEditor';
import {
	adjustHeadersToStartingLevel,
	type HeaderLevel,
	headerNumericalLevels,
} from '@ivy/lib/styling/header';
import { combineSx } from '@ivy/lib/styling/sx';

export interface ReadOnlyTextEditorProps {
	/**
	 * The HTML content to display
	 */
	content: string;
	/**
	 * Shift the headers to start from a different level in order to avoid conflicts elsewhere on the page.
	 * For example, `startingHeaderLevel=3` will move h1 -> h3, h2 -> h4, h3 -> h5, h4 -> h6, h5 -> h6, and h6 -> h6.
	 */
	startingHeaderLevel?: HeaderLevel;
	/**
	 * Shift the styling of the headers to start at the specified level. Works in combination with
	 * `startingHeaderLevel`. For example, `startingHeaderLevel=3` and `startingHeaderLevelStyling=5` will
	 * shift h1 -> h3 with h5 styling, h2 -> h4 with h6 styling. Everything else will have h3 styling if
	 * it's bigger than h3 or h6 styling if it's smaller than h4.
	 * */
	startingHeaderLevelStyles?: HeaderLevel;
	/** Styles */
	sx?: SxProps<Theme>;
}

const ReadOnlyTextEditor = ({
	content,
	startingHeaderLevel = headerNumericalLevels[headerNumericalLevels.length - 1],
	startingHeaderLevelStyles = headerNumericalLevels[
		headerNumericalLevels.length - 1
	],
	sx,
}: ReadOnlyTextEditorProps) => {
	const extensions = useExtensions();
	const cleaned = useMemo(() => {
		// Sanitize the HTML to avoid XSS attacks
		const sanitized = DOMPurify.sanitize(content, {
			ALLOWED_TAGS: [
				'h1',
				'h2',
				'h3',
				'h4',
				'h5',
				'h6',
				'p',
				'b',
				'strong',
				'em',
				'i',
				'u',
				'a',
				'span',
				'div',
				'img',
				// 'video',
				// 'source',
				'table',
				'tbody',
				'td',
				'tfoot',
				'th',
				'thead',
				'tr',
				'ol',
				'ul',
				'li',
				'br',
				'hr',
				'blockquote',
				'pre',
			],
			ALLOWED_ATTR: [
				// links
				// TODO: force links to open in a new tab
				'target',
				'href',
				'rel',
				// images
				'src',
				'width',
				'height',
				'alt',
				// video
				// 'controls',
				// other
				'type',
				// for things like text-align, although there could be some more nefarious ones like positioning
				// TODO: disable absolute/fixed positioning. Can disbale absolute with a relative parent, and fixed
				//   with an absolute parent nested under the relative. Perhaps tiptap already takes care of this?
				'style',
			],
		});

		// Convert headings to start from `startingHeaderLevel` if requested
		if (!startingHeaderLevel) {
			return sanitized;
		}
		return adjustHeadersToStartingLevel(sanitized, startingHeaderLevel);
	}, [content, startingHeaderLevel]);

	return (
		<Box
			sx={combineSx(
				(theme) => ({
					'&& .ProseMirror': {
						'& h1, & h2, & h3, & h4, & h5, & h6, & p, & ul, & ol': {
							marginBlockStart: 0,
							marginBlockEnd: 'revert',
						},
						'& table p': {
							margin: 0,
						},
						'& ul[data-type="taskList"] p': {
							margin: 0,
						},
						...headerNumericalLevels.reduce((tot, curr) => {
							const adjustment =
								startingHeaderLevelStyles - startingHeaderLevel;
							const level = Math.max(
								// Bounded by `startingHeaderLevelStyling` and h6
								Math.min(curr + adjustment, headerNumericalLevels[0]),
								startingHeaderLevelStyles,
							);
							return {
								...tot,
								[`& h${curr}`]: {
									...theme.typography[`h${level}`],
								},
							};
						}, {}),
					},
				}),
				sx,
			)}
		>
			<RichTextReadOnly extensions={extensions} content={cleaned} />
		</Box>
	);
};

export default ReadOnlyTextEditor;
